import React, { FC, useMemo } from 'react';
import sanitizeContent from '@sv-support-sdk/utils/src/helpers/sanitizeContent';
import {
  MESSAGE_SUPPORT_AGENT_TYPE,
  MESSAGE_USER_TYPES_LIST,
  MESSAGE_TYPES,
} from '@sv-support-sdk/utils/src/constants/chatMessages';
import fileSizeFormat from '@sv-support-sdk/utils/src/helpers/fileSizeFormat';
import { formatDate } from '@sv-support-sdk/utils/src/helpers/toLocaleTime';
import { divideMessageAndReply, withoutHTML } from '@sv-support-sdk/utils/src/helpers/chatMessage';
import { IMessage } from '@sv-support-sdk/chat-shared-types/src/common/messages';

import Message from '../../ui/Content/Message';
import MessageFile from '../../ui/Content/Message/MessageFile';
import Reply from '../../ui/Content/Reply';
import { MESSAGE_ID_NAME } from '../../../constants/messages';
import { $l } from '../../../locales';
import { useAppDispatch } from '../../../store/hooks';
import { errorResend } from '../../../store/actions/messages';
import { setReplyToSupport } from '../../../store/slices/messages';

interface IMessageTextBlock {
  message: IMessage;
}

const MessageText: FC<IMessageTextBlock> = ({ message }) => {
  const dispatch = useAppDispatch();
  const type = MESSAGE_USER_TYPES_LIST.includes(message.authorType) ? 'fromUser' : undefined;
  const avatar = message.img
    ? { img: { src: message.img } }
    : !type
      ? { icon: 'colored--32--support-agent' }
      : undefined;
  const date = formatDate(message.createdAt, 'hh:mm A');

  const messageData = useMemo(() => divideMessageAndReply(message.message), [message.message]);
  const resend = () => {
    dispatch(errorResend(message));
  };

  const replyAction = () => {
    const text = withoutHTML(messageData.message);
    const reply = text.length < 100 ? text : `${text.slice(0, 100)}...`;
    dispatch(setReplyToSupport(reply));
  };

  const replyIsActive =
    message.authorType === MESSAGE_SUPPORT_AGENT_TYPE && message.type !== MESSAGE_TYPES.TRANSFER;

  return (
    <Message
      id={`${MESSAGE_ID_NAME}${message.id}`}
      error={message.error}
      avatar={avatar}
      type={type}
      date={date}
      withReply={replyIsActive}
      errorResend={resend}
      replyAction={replyAction}
    >
      {messageData.reply && (
        <Reply>
          <span dangerouslySetInnerHTML={sanitizeContent(messageData.reply)} />
        </Reply>
      )}
      <span dangerouslySetInnerHTML={sanitizeContent(messageData.message)} />
      {message.attachments &&
        message.attachments.map((el, index) => (
          <MessageFile
            key={`${el.name}_${el.id}`}
            name={el.name}
            link={el.link}
            extraIcon={el.link ? 'colored--12--download' : ''}
            extension={el.extension}
            subtitle={el.link && $l('CLICK_TO_DOWNLOAD')}
            size={fileSizeFormat(el.size || 0)}
            loading={index === message.attachments.length - 1 ? message.loading : 0}
          />
        ))}
    </Message>
  );
};

export default MessageText;
